import { useLocalStorage } from "@mantine/hooks";
import { z } from "zod";
import type { DocumentId } from "./document-id/base-document-id";

export const LOCAL_FIRST_YJS_STORAGE_KEY =
	"illumi-experimental-local-first-yjs";

const localFirstYjsEnabledSchema = z.boolean();

export type LocalFirstYjsEnabled = z.infer<typeof localFirstYjsEnabledSchema>;

const LOCAL_FIRST_STORAGE_OPTIONS = {
	key: LOCAL_FIRST_YJS_STORAGE_KEY,
	defaultValue: false,
	getInitialValueInEffect: false,
};

const locallyCreatedDocumentIds = new Set<string>();

/**
 * Synchronous reader for the Experimental Local-First Yjs Sync developer setting.
 * Defaults to `false` (disabled) so standard production behavior is preserved
 * unless explicitly opted in via Profile -> Developer Settings.
 */
export function isLocalFirstYjsEnabled(): boolean {
	if (typeof window === "undefined" || !window.localStorage) {
		return false;
	}

	try {
		const raw = window.localStorage.getItem(LOCAL_FIRST_YJS_STORAGE_KEY);
		if (raw === null) {
			return false;
		}
		if (raw === "true") {
			return true;
		}
		const parsed = localFirstYjsEnabledSchema.safeParse(JSON.parse(raw));
		return parsed.success && parsed.data;
	} catch {
		return false;
	}
}

export function setLocalFirstYjsEnabled(enabled: boolean): void {
	if (typeof window === "undefined" || !window.localStorage) {
		return;
	}

	try {
		window.localStorage.setItem(
			LOCAL_FIRST_YJS_STORAGE_KEY,
			JSON.stringify(enabled),
		);
	} catch {
		// Ignore localStorage quota/security errors
	}
}

export function useLocalFirstYjsSetting() {
	const [enabled, setEnabled] = useLocalStorage<boolean>(
		LOCAL_FIRST_STORAGE_OPTIONS,
	);

	return {
		enabled,
		setEnabled,
	};
}

/**
 * Marks a document ID as freshly generated on this client during the current session.
 * When Experimental Local-First Yjs Sync is enabled, `getDocument()` immediately
 * transitions locally created documents to `"synced-local"` at 0ms so the user can
 * start typing without waiting for a server round-trip.
 */
export function markLocallyCreatedDocument(
	documentId: DocumentId | string,
): void {
	if (!isLocalFirstYjsEnabled()) {
		return;
	}
	locallyCreatedDocumentIds.add(documentId.toString());
}

export function consumeLocallyCreatedDocument(
	documentId: DocumentId | string,
): boolean {
	if (!isLocalFirstYjsEnabled()) {
		return false;
	}
	const key = documentId.toString();
	if (locallyCreatedDocumentIds.has(key)) {
		locallyCreatedDocumentIds.delete(key);
		return true;
	}
	return false;
}

export function clearLocallyCreatedDocuments(): void {
	locallyCreatedDocumentIds.clear();
}
